import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import { isHeic } from '@/lib/image-pipeline/heic-converter';
import { computeHash } from '@/lib/image-pipeline/perceptual-hash';
import type { OverlayLayer } from '@/lib/media-studio/overlay-types';
import {
  type UploadConversion,
  uploadConversion,
  uploadConversionNotice,
} from '@/services/media/upload-conversion';
import { ContentType } from '@/types/contentTypes';
// F-082: imageCompression pulls browser-image-compression (~280KB). Lazy-load
// it inside the one method that compresses, so mediaService's many eager
// consumers don't drag that dep into the root entry graph.
import type { MediaFile } from '@/types/studio';
import { captureVideoPoster } from '@/utils/captureVideoPoster';
import { formatFileSize } from '@/utils/format';
import { logger } from '@/utils/logger';
import { validateFileSizeAndFormat } from '@/utils/mediaValidation';

interface MediaUploadResult {
  id: string;
  url: string;
  publicUrl: string;
  mediaType: 'image' | 'video';
  coverImageUrl: string | null;
  width: number | null;
  height: number | null;
  conversionNotice: string | null;
  conversion?: UploadConversion | null;
}

interface MediaRecord {
  id: string;
  user_id: string;
  file_name: string;
  url: string;
  type: string;
  file_size: number;
  width?: number;
  height?: number;
  alt_text?: string;
  folder?: string;
  collection_id?: string;
  tags?: string[];
  metadata?: unknown;
  created_at: string;
  storage_bucket?: string;
  duration_seconds?: number;
  aspect_ratio?: string;
  source?: string;
  source_ref?: string;
  cover_image_url?: string;
}

export const GENERATED_CONTENT_BUCKET = 'generated-content';

/**
 * The workspace (brand kit) this user is currently acting in, or null.
 *
 * Same pattern as `PostService.resolveActiveBrandKitId` / historically
 * `CampaignService.resolveActiveBrandKitId` — always via
 * `get_effective_brand_kit()`, the zero-arg `authenticated` entry point that
 * resolves through `auth.uid()`. Never re-derive the rule here: it lives once,
 * in `resolve_effective_brand_kit_id()` in Postgres (this user's pointer ->
 * the account default -> most recent). The actor-taking variant
 * (`get_effective_brand_kit_for`) is service_role only and must never be
 * reachable from a browser client.
 */
async function resolveActiveBrandKitId(): Promise<string | null> {
  try {
    const { data, error } = await supabase.rpc('get_effective_brand_kit');
    if (error || !Array.isArray(data) || data.length === 0) return null;
    return (data[0] as { id?: string }).id ?? null;
  } catch {
    return null;
  }
}

class MediaService {
  private readonly BUCKET_NAME = 'post_media';

  /**
   * Convert a stored Supabase public/authenticated URL back to the object path
   * required by `storage.remove`. Generated media used to persist full public
   * URLs, while current writes persist raw paths, so delete must support both
   * representations without ever passing a third-party URL to Storage.
   */
  private storagePathForBucket(bucket: string, value: string | null | undefined): string | null {
    if (!value) return null;
    if (!/^https?:\/\//i.test(value)) return value;

    try {
      const url = new URL(value);
      const prefixes = [
        `/storage/v1/object/public/${bucket}/`,
        `/storage/v1/object/authenticated/${bucket}/`,
      ];
      const prefix = prefixes.find((candidate) => url.pathname.startsWith(candidate));
      return prefix ? url.pathname.slice(prefix.length) : null;
    } catch {
      return null;
    }
  }

  /**
   * Upload a file to Supabase Storage and create a media record
   */
  async uploadFile(
    rawFile: File,
    options?: {
      folder?: string;
      collectionId?: string;
      tags?: string[];
      altText?: string;
      parentMessageId?: string;
      originSessionId?: string;
    },
  ): Promise<MediaUploadResult> {
    let file = rawFile;
    if (isHeic(rawFile)) {
      try {
        const { convertHeicToJpeg } = await import('@/lib/image-pipeline/heic-converter');
        file = await convertHeicToJpeg(rawFile);
      } catch (err) {
        logger.error('HEIC conversion failed', err);
        throw new Error(
          `${rawFile.name} could not be converted. Export it as a JPG or PNG and try again.`,
        );
      }
    }
    const preCompression = file;
    // Compress user-uploaded images BEFORE validation (research 2026-07-08:
    // competitors cap at 5-25MB and Later auto-compresses >8MB; IG's Graph
    // API is JPEG-only at 8MB, so JPEG@2048px is the real publishable
    // target). A 25MB phone photo lands ~1.5MB. gif keeps animation, svg is
    // vector, heic can't be canvas-decoded cross-browser — those pass
    // through raw and hit the validation cap as-is. Best-effort: a failed
    // compression falls back to the original.
    if (
      file.type.startsWith('image/') &&
      !['image/svg+xml', 'image/gif', 'image/heic', 'image/heif'].includes(file.type)
    ) {
      try {
        const { compressImage, getRecommendedQuality } =
          await import('@/services/media/imageCompression');
        file = await compressImage(file, {
          quality: getRecommendedQuality(file.size, false),
          format: 'jpeg',
          useWebWorker: true,
        });
      } catch (err) {
        logger.error('upload compression failed — using original file', err);
        file = preCompression;
      }
    }
    const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
    const validation = validateFileSizeAndFormat(file, ContentType.SOCIAL_POST, mediaType);
    if (!validation.isValid) {
      throw new Error(
        validation.error?.startsWith('Unsupported format')
          ? `${file.name} is not a supported file type. Use JPG, PNG, WebP, GIF, HEIC or MP4.`
          : (validation.error ?? `${file.name} is not a supported file`),
      );
    }

    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    // Storage path stays keyed on the raw session user id — the
    // `post_media` bucket's storage.objects RLS still checks raw
    // `auth.uid() = owner` (untouched by the 20260424 rewrite), so this is
    // NOT part of the shared-account bug. Only the `media` table row below
    // needs the resolved owner id.
    const ownerId = await resolveOwnerId(user.id);

    // Generate unique file name
    const timestamp = Date.now();
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${user.id}/${timestamp}-${sanitizedName}`;

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from(this.BUCKET_NAME)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      logger.error('Upload error details:', {
        error: uploadError,
        fileName,
        bucket: this.BUCKET_NAME,
        fileType: file.type,
        fileSize: file.size,
      });
      throw new Error(`Upload failed: ${uploadError.message}. Path: ${fileName}`);
    }

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from(this.BUCKET_NAME).getPublicUrl(fileName);

    // Get image dimensions if it's an image
    let width: number | undefined;
    let height: number | undefined;

    // Compute perceptual hash for image files (used by cluster_photos workflow tool).
    let phash: string | undefined;
    if (file.type.startsWith('image/')) {
      const dimensions = await this.getImageDimensions(file);
      width = dimensions.width;
      height = dimensions.height;

      try {
        phash = await computeHash(file);
      } catch (err) {
        logger.error('phash computation failed', err);
      }
    }

    // Videos without a cover render as black tiles in every media grid —
    // capture a poster frame client-side and store it alongside the clip.
    // Best-effort: any capture/upload failure leaves cover_image_url null.
    let coverImageUrl: string | undefined;
    if (mediaType === 'video') {
      try {
        const poster = await captureVideoPoster(file);
        if (poster) {
          const posterPath = `${user.id}/${timestamp}-${sanitizedName}.poster.jpg`;
          const { error: posterError } = await supabase.storage
            .from(this.BUCKET_NAME)
            .upload(posterPath, poster, {
              cacheControl: '3600',
              upsert: false,
              contentType: 'image/jpeg',
            });
          if (!posterError) {
            coverImageUrl = supabase.storage.from(this.BUCKET_NAME).getPublicUrl(posterPath)
              .data.publicUrl;
          }
        }
      } catch (err) {
        logger.error('video poster capture failed', err);
      }
    }

    // Create media record in database
    const collectionId = options?.collectionId || null;
    // Stamp the active workspace at creation, same pattern as
    // PostService.createPost — new rows are attributable, while
    // pre-workspace-stamping rows stay NULL (legacy/account-wide).
    const activeKitId = await resolveActiveBrandKitId();

    const { data: mediaRecord, error: dbError } = await supabase
      .from('media')
      .insert({
        user_id: ownerId,
        brand_kit_id: activeKitId,
        file_name: file.name,
        url: fileName,
        type: file.type,
        mime_type: file.type,
        file_size: file.size,
        width,
        height,
        alt_text: options?.altText,
        folder: options?.folder ?? null,
        cover_image_url: coverImageUrl ?? null,
        collection_id: collectionId,
        tags: options?.tags,
        parent_message_id: options?.parentMessageId ?? null,
        // Phase 2b: which chat session this upload originated from (when known).
        origin_session_id: options?.originSessionId ?? null,
        metadata: {
          storage_path: fileName,
          originalName: file.name,
          uploadedAt: new Date().toISOString(),
          ...(phash ? { phash } : {}),
        },
      })
      .select()
      .single();

    if (dbError) {
      logger.error('Database insert error:', {
        error: dbError,
        table: 'media',
        data: {
          user_id: user.id,
          file_name: file.name,
          url: fileName,
          type: file.type,
          file_size: file.size,
        },
      });
      // Clean up storage if database insert fails
      await supabase.storage.from(this.BUCKET_NAME).remove([fileName]);
      throw new Error(`Failed to create media record: ${dbError.message}`);
    }

    return {
      id: mediaRecord.id,
      url: fileName,
      publicUrl,
      mediaType,
      coverImageUrl: coverImageUrl ?? null,
      width: width ?? null,
      height: height ?? null,
      conversionNotice: uploadConversionNotice(rawFile, file),
      conversion: uploadConversion(rawFile, file),
    };
  }

  /**
   * Upload multiple files
   */
  async uploadMultiple(
    files: File[],
    options?: {
      folder?: string;
      collectionId?: string;
      tags?: string[];
    },
  ): Promise<MediaUploadResult[]> {
    const results: MediaUploadResult[] = [];

    for (const file of files) {
      try {
        const result = await this.uploadFile(file, options);
        results.push(result);
      } catch (error) {
        logger.error(`Failed to upload ${file.name}:`, error);
        // Continue uploading other files
      }
    }

    return results;
  }

  /**
   * Upload a transformed image blob to the generated-content bucket.
   * This path is used by the inspector image editor and is intentionally
   * isolated from user uploads so it cannot affect upload metadata and ownership
   * assumptions in the standard upload flow.
   */
  async uploadGeneratedContentBlob(
    blob: Blob,
    filename: string,
    contentType = 'image/webp',
  ): Promise<string> {
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(GENERATED_CONTENT_BUCKET)
      .upload(filename, blob, { contentType, upsert: false });
    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}. Path: ${filename}`);
    }

    return supabase.storage.from(GENERATED_CONTENT_BUCKET).getPublicUrl(uploadData.path).data
      .publicUrl;
  }

  async registerExternalUpload(input: {
    storagePath: string;
    publicUrl: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
    folder?: string;
    collectionId?: string;
    tags?: string[];
    metadata?: Record<string, unknown>;
  }): Promise<MediaUploadResult> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const ownerId = await resolveOwnerId(user.id);

    let phash: string | undefined;
    if (input.mimeType.startsWith('image/')) {
      try {
        const res = await fetch(input.publicUrl);
        if (res.ok) {
          const blob = await res.blob();
          const file = new File([blob], input.fileName, { type: input.mimeType });
          phash = await computeHash(file);
        }
      } catch (err) {
        logger.error('registerExternalUpload phash computation failed', err);
      }
    }

    const activeKitId = await resolveActiveBrandKitId();
    const externalMetadata = { ...(input.metadata ?? {}) };
    delete externalMetadata.storage_path;
    delete externalMetadata.path;

    const { data: mediaRecord, error: dbError } = await supabase
      .from('media')
      .insert({
        user_id: ownerId,
        brand_kit_id: activeKitId,
        file_name: input.fileName,
        url: input.storagePath,
        type: input.mimeType,
        mime_type: input.mimeType,
        file_size: input.fileSize,
        folder: input.folder ?? null,
        collection_id: input.collectionId || null,
        tags: input.tags,
        metadata: {
          ...externalMetadata,
          storage_path: input.storagePath,
          originalName: input.fileName,
          uploadedAt: new Date().toISOString(),
          ...(phash ? { phash } : {}),
        },
      })
      .select()
      .single();

    if (dbError) {
      await supabase.storage.from(this.BUCKET_NAME).remove([input.storagePath]);
      throw new Error(`Failed to create media record: ${dbError.message}`);
    }

    return {
      id: mediaRecord.id,
      url: input.storagePath,
      publicUrl: input.publicUrl,
      mediaType: input.mimeType.startsWith('video/') ? 'video' : 'image',
      coverImageUrl:
        typeof mediaRecord.cover_image_url === 'string' ? mediaRecord.cover_image_url : null,
      width: typeof mediaRecord.width === 'number' ? mediaRecord.width : null,
      height: typeof mediaRecord.height === 'number' ? mediaRecord.height : null,
    };
  }

  async getUserMedia(options?: {
    folder?: string;
    collectionId?: string;
    tags?: string[];
    limit?: number;
    offset?: number;
  }): Promise<{ data: MediaRecord[]; count: number }> {
    let query = supabase
      .from('media')
      .select('*', { count: 'exact' })
      .neq('file_name', '.folder_placeholder')
      .is('metadata->>derivedFromMediaId', null);

    // Scope to the active workspace, keeping unattributed (legacy,
    // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
    // This is the primary Library browse surface, so this is the filter
    // that actually matters for the multi-workspace asset bleed.
    const activeKitId = await resolveActiveBrandKitId();
    if (activeKitId) {
      query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
    }

    if (options?.collectionId) {
      query = query.eq('collection_id', options.collectionId);
    } else if (options?.folder) {
      query = query.eq('folder', options.folder);
    }

    if (options?.tags && options.tags.length > 0) {
      query = query.contains('tags', options.tags);
    }

    query = query
      .order('created_at', { ascending: false })
      .range(options?.offset || 0, (options?.offset || 0) + (options?.limit || 20) - 1);

    const { data, error, count } = await query;

    if (error) {
      throw new Error(`Failed to fetch media: ${error.message}`);
    }

    return { data: data || [], count: count || 0 };
  }

  /**
   * Count `social_posts` rows (usually curation-flow drafts, see
   * CreateDraftsFromMediaService) that reference this media item's PUBLIC
   * URL in `media_urls` or `cover_image_url`. `deleteMedia` has no FK to
   * guard it — a deleted-but-still-referenced draft is left with a
   * permanently 404 image and no warning at delete time. Callers should
   * check this before deleting and confirm with the user when > 0.
   * Fail-open (returns 0) on lookup error — a transient failure here must
   * never block a delete the user actually asked for.
   */
  async getReferencingPostCount(mediaId: string): Promise<number> {
    try {
      const { data: mediaRow, error } = await supabase
        .from('media')
        .select('url, storage_bucket')
        .eq('id', mediaId)
        .maybeSingle();
      if (error || !mediaRow) return 0;

      const bucket = mediaRow.storage_bucket || this.BUCKET_NAME;
      const publicUrl = /^https?:\/\//i.test(mediaRow.url)
        ? mediaRow.url
        : supabase.storage.from(bucket).getPublicUrl(mediaRow.url).data.publicUrl;

      return await this.getReferencingPostCountForUrls([publicUrl]);
    } catch (err) {
      logger.error('getReferencingPostCount failed', err);
      return 0;
    }
  }

  /**
   * Bulk variant of `getReferencingPostCount` — one query pair covering
   * every url instead of N. Callers pass already-resolved public URLs.
   */
  async getReferencingPostCountForUrls(urls: string[]): Promise<number> {
    if (urls.length === 0) return 0;
    try {
      const [byMediaUrls, byCover] = await Promise.all([
        supabase.from('social_posts').select('id').overlaps('media_urls', urls),
        supabase.from('social_posts').select('id').in('cover_image_url', urls),
      ]);
      if (byMediaUrls.error || byCover.error) {
        logger.error(
          'getReferencingPostCountForUrls: reference query failed',
          byMediaUrls.error ?? byCover.error,
        );
      }
      const ids = new Set<string>();
      for (const row of byMediaUrls.data ?? []) ids.add(row.id);
      for (const row of byCover.data ?? []) ids.add(row.id);
      return ids.size;
    } catch (err) {
      logger.error('getReferencingPostCountForUrls failed', err);
      return 0;
    }
  }

  /**
   * Bulk-delete-flow variant of `getReferencingPostCount` — takes media ids
   * (not pre-resolved URLs), resolves them all, and checks in one pass.
   */
  async getReferencingPostCountForMediaIds(mediaIds: string[]): Promise<number> {
    if (mediaIds.length === 0) return 0;
    try {
      const { data: mediaRows, error } = await supabase
        .from('media')
        .select('url, storage_bucket')
        .in('id', mediaIds);
      if (error || !mediaRows) return 0;

      const urls = mediaRows.map((row) => {
        const bucket = row.storage_bucket || this.BUCKET_NAME;
        return /^https?:\/\//i.test(row.url)
          ? row.url
          : supabase.storage.from(bucket).getPublicUrl(row.url).data.publicUrl;
      });
      return await this.getReferencingPostCountForUrls(urls);
    } catch (err) {
      logger.error('getReferencingPostCountForMediaIds failed', err);
      return 0;
    }
  }

  /**
   * Delete media file and record
   */
  async deleteMedia(mediaId: string): Promise<void> {
    // Get media record first
    const { data: media, error: fetchError } = await supabase
      .from('media')
      .select('url, storage_bucket, cover_image_url')
      .eq('id', mediaId)
      .single();

    if (fetchError || !media) {
      throw new Error(`Media not found: ${fetchError?.message ?? 'media record is missing'}`);
    }

    const bucket = media.storage_bucket || this.BUCKET_NAME;
    const originalPath = this.storagePathForBucket(bucket, media.url);
    if (!originalPath) {
      throw new Error(`Unable to resolve Storage path for media ${mediaId}`);
    }

    const thumbnailPath = this.storagePathForBucket(bucket, media.cover_image_url);
    const storagePaths = [
      ...new Set([originalPath, thumbnailPath].filter((path): path is string => Boolean(path))),
    ];

    // Storage must succeed before the metadata row is removed. Continuing on
    // an RLS/network failure creates an untracked, billable object that can no
    // longer be reached from the Library to retry deletion.
    const { error: storageError } = await supabase.storage.from(bucket).remove(storagePaths);

    if (storageError) {
      throw new Error(`Failed to delete media storage: ${storageError.message}`);
    }

    // Delete from database
    const { error: dbError } = await supabase.from('media').delete().eq('id', mediaId);

    if (dbError) {
      throw new Error(`Failed to delete media record: ${dbError.message}`);
    }
  }

  /**
   * Update media metadata
   */
  async updateMedia(
    mediaId: string,
    updates: {
      alt_text?: string;
      folder?: string;
      tags?: string[];
    },
  ): Promise<void> {
    const { error } = await supabase.from('media').update(updates).eq('id', mediaId);

    if (error) {
      throw new Error(`Failed to update media: ${error.message}`);
    }
  }

  /**
   * Repoint a media row at a new image file — the persistence half of the
   * Inspector's destructive transforms (crop / rotate / flip). Without this
   * the transform uploads a new storage object but the row (and therefore
   * the Library, posts, and every other consumer) keeps serving the old
   * pixels (editor-QA P0, 2026-07-03). Stores the storage PATH when the URL
   * is a generated-content public URL (matching the generate-image route's
   * convention) so resolveStorageUrl keeps working for both forms.
   * Returns false when no row matched (e.g. chat artifacts that never had a
   * media row) — callers treat that as "session-only edit", not an error.
   */
  async updateMediaUrl(mediaId: string, url: string): Promise<boolean> {
    const pathMatch = url.match(/\/generated-content\/(.+)$/);
    const storedUrl = pathMatch ? pathMatch[1] : url;
    const { data, error } = await supabase
      .from('media')
      .update({ url: storedUrl, updated_at: new Date().toISOString() })
      .eq('id', mediaId)
      .select('id');

    if (error) throw new Error(`Failed to update media url: ${error.message}`);
    return !!data && data.length > 0;
  }

  /**
   * Persist overlay layers (text, stickers) as JSON on a media row.
   * Does NOT composite — layers are baked on export only.
   * Returns the updated row so RLS denials (no rows matched) surface as
   * an error instead of a silent no-op.
   */
  async updateOverlayLayers(mediaId: string, layers: OverlayLayer[]): Promise<void> {
    const { data, error } = await supabase
      .from('media')
      .update({ overlay_layers: layers as unknown as Json })
      .eq('id', mediaId)
      .select('id');

    if (error) throw new Error(`Failed to save overlay layers: ${error.message}`);
    if (!data || data.length === 0) {
      throw new Error(
        `Failed to save overlay layers: no row updated (media ${mediaId} not found or not owned by you)`,
      );
    }
  }

  /**
   * Fetch overlay layers for a media row.
   */
  async getOverlayLayers(mediaId: string): Promise<OverlayLayer[]> {
    const { data, error } = await supabase
      .from('media')
      .select('overlay_layers')
      .eq('id', mediaId)
      .single();

    if (error) throw new Error(`Failed to fetch overlay layers: ${error.message}`);
    return (data?.overlay_layers as unknown as OverlayLayer[] | null) ?? [];
  }

  /**
   * Get folders for the current user
   */
  async getUserFolders(): Promise<string[]> {
    let query = supabase.from('media_collections').select('name');

    // Scope to the active workspace, keeping unattributed (legacy,
    // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
    const activeKitId = await resolveActiveBrandKitId();
    if (activeKitId) {
      query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
    }

    const { data, error } = await query.order('name', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch folders: ${error.message}`);
    }

    return (data || []).map((item) => item.name);
  }

  async getUserFoldersWithDetails(includeArchived = false): Promise<
    Array<{
      id: string;
      name: string;
      count: number;
      planCount: number;
      postCount: number;
      coverUrl?: string;
      icon?: string;
      color?: string;
      createdAt?: string;
      updatedAt?: string;
      parentId?: string;
      archivedAt?: string;
      position: number;
    }>
  > {
    let query = supabase
      .from('media_collections')
      .select('id, name, icon, color, created_at, updated_at, parent_id, archived_at, position');

    if (!includeArchived) {
      query = query.is('archived_at', null);
    }

    // Scope to the active workspace, keeping unattributed (legacy,
    // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
    const activeKitId = await resolveActiveBrandKitId();
    if (activeKitId) {
      query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
    }

    const { data: collections, error: collectionsError } = await query
      .order('position', { ascending: true })
      .order('updated_at', { ascending: false });

    if (collectionsError) {
      throw new Error(`Failed to fetch collections: ${collectionsError.message}`);
    }

    const collectionIds = (collections || []).map((c) => c.id);
    if (collectionIds.length === 0) {
      return (collections || []).map((c) => ({
        id: c.id,
        name: c.name,
        count: 0,
        planCount: 0,
        postCount: 0,
        icon: c.icon || undefined,
        color: c.color || undefined,
        createdAt: c.created_at || undefined,
        parentId: (c as { parent_id?: string }).parent_id || undefined,
        archivedAt: (c as { archived_at?: string }).archived_at || undefined,
        position: (c as { position?: number }).position ?? 0,
      }));
    }

    const { data: mediaFiles, error: mediaError } = await supabase
      .from('media')
      .select('collection_id, url')
      .in('collection_id', collectionIds);

    if (mediaError) {
      throw new Error(`Failed to fetch folder media details: ${mediaError.message}`);
    }

    const { data: postRows } = await supabase
      .from('social_posts')
      .select('collection_id')
      .in('collection_id', collectionIds);

    const { data: campaignRows } = await supabase
      .from('projects')
      .select('collection_id')
      .in('collection_id', collectionIds);

    const mediaLookup = new Map<string, { count: number; coverUrl?: string }>();
    mediaFiles?.forEach((file) => {
      if (!file.collection_id) return;
      const existing = mediaLookup.get(file.collection_id) || { count: 0 };
      existing.count++;
      if (!existing.coverUrl) {
        existing.coverUrl = supabase.storage
          .from(this.BUCKET_NAME)
          .getPublicUrl(file.url).data.publicUrl;
      }
      mediaLookup.set(file.collection_id, existing);
    });

    const postCountLookup = new Map<string, number>();
    postRows?.forEach((post) => {
      if (!post.collection_id) return;
      postCountLookup.set(post.collection_id, (postCountLookup.get(post.collection_id) || 0) + 1);
    });

    const planCountLookup = new Map<string, number>();
    campaignRows?.forEach((campaign) => {
      if (!campaign.collection_id) return;
      planCountLookup.set(
        campaign.collection_id,
        (planCountLookup.get(campaign.collection_id) || 0) + 1,
      );
    });

    return (collections || []).map((c) => {
      const typed = c as typeof c & {
        parent_id?: string;
        archived_at?: string;
        position?: number;
        updated_at?: string;
      };
      const mediaInfo = mediaLookup.get(c.id) || { count: 0 };
      const posts = postCountLookup.get(c.id) || 0;
      const plans = planCountLookup.get(c.id) || 0;
      return {
        id: c.id,
        name: c.name,
        count: mediaInfo.count + posts,
        planCount: plans,
        postCount: posts,
        coverUrl: mediaInfo.coverUrl,
        icon: c.icon,
        color: c.color,
        createdAt: c.created_at,
        updatedAt: typed.updated_at || c.created_at,
        parentId: typed.parent_id || undefined,
        archivedAt: typed.archived_at || undefined,
        position: typed.position ?? 0,
      };
    });
  }

  private async setFolderArchivedAt(
    folderId: string,
    userId: string,
    value: string | null,
  ): Promise<void> {
    // `userId` arrives from callers (useMediaLibrary) as the raw session
    // user id — resolve to the shared-account owner id before filtering,
    // since `media_collections.user_id` is always the owner's id. Without
    // this a team member's archive/unarchive silently matched zero rows.
    const ownerId = await resolveOwnerId(userId);
    const { error } = await supabase
      .from('media_collections')
      .update({ archived_at: value })
      .or(`id.eq.${folderId},parent_id.eq.${folderId}`)
      .eq('user_id', ownerId);
    if (error) throw error;
  }

  async archiveFolder(folderId: string, userId: string): Promise<void> {
    await this.setFolderArchivedAt(folderId, userId, new Date().toISOString());
  }

  async unarchiveFolder(folderId: string, userId: string): Promise<void> {
    await this.setFolderArchivedAt(folderId, userId, null);
  }

  async createFolder(
    name: string,
    icon?: string,
    color?: string,
    parentId?: string,
  ): Promise<{ id: string; name: string }> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const ownerId = await resolveOwnerId(user.id);

    if (parentId) {
      const canNest = await this.canNestUnder(parentId, ownerId);
      if (!canNest) throw new Error('Cannot nest here — maximum folder depth reached');
    }

    const insertData: Record<string, unknown> = {
      user_id: ownerId,
      brand_kit_id: await resolveActiveBrandKitId(),
      name,
      icon: icon || 'folder',
      color: color || 'slate',
    };

    if (parentId) {
      insertData.parent_id = parentId;
    }

    const { data, error } = await supabase
      .from('media_collections')
      .insert(insertData)
      .select('id, name')
      .single();

    if (error) {
      if (error.code === '23505') {
        const { data: existing } = await supabase
          .from('media_collections')
          .select('id, name')
          .eq('user_id', ownerId)
          .eq('name', name)
          .single();
        if (existing) return existing;
      }
      throw new Error(`Failed to create folder: ${error.message}`);
    }
    if (!data) throw new Error('Failed to create folder: no data returned');
    return data;
  }

  async updateFolder(
    collectionId: string,
    updates: { name?: string; icon?: string; color?: string },
  ): Promise<void> {
    const dbUpdates: Record<string, string> = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.icon !== undefined) dbUpdates.icon = updates.icon;
    if (updates.color !== undefined) dbUpdates.color = updates.color;

    if (Object.keys(dbUpdates).length > 0) {
      const { error } = await supabase
        .from('media_collections')
        .update(dbUpdates)
        .eq('id', collectionId);

      if (error) throw new Error(`Failed to update collection: ${error.message}`);
    }
  }

  async deleteFolder(collectionId: string, deleteContents = false): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const ownerId = await resolveOwnerId(user.id);

    const { data: owner } = await supabase
      .from('media_collections')
      .select('user_id')
      .eq('id', collectionId)
      .single();
    if (!owner || owner.user_id !== ownerId)
      throw new Error('Not authorized to delete this collection');

    const { data: childCollections } = await supabase
      .from('media_collections')
      .select('id')
      .eq('parent_id', collectionId);

    const allCollectionIds = [collectionId, ...(childCollections?.map((c) => c.id) || [])];

    if (deleteContents) {
      for (const cId of allCollectionIds) {
        const { data: items } = await supabase
          .from('media')
          .select('id, url')
          .eq('collection_id', cId);

        if (items && items.length > 0) {
          const urls = items.map((i) => i.url);
          const ids = items.map((i) => i.id);
          await supabase.storage.from(this.BUCKET_NAME).remove(urls);
          await supabase.from('media').delete().in('id', ids);
        }

        await Promise.all([
          supabase.from('social_posts').delete().eq('collection_id', cId),
          supabase.from('projects').delete().eq('collection_id', cId),
        ]);
      }
    } else {
      for (const cId of allCollectionIds) {
        await supabase.from('media').update({ collection_id: null }).eq('collection_id', cId);
      }
    }

    const { error: deleteError } = await supabase
      .from('media_collections')
      .delete()
      .eq('id', collectionId);

    if (deleteError) throw new Error(`Failed to delete collection: ${deleteError.message}`);
  }

  async canNestUnder(parentId: string, userId: string): Promise<boolean> {
    const ownerId = await resolveOwnerId(userId);
    const { data: parent } = await supabase
      .from('media_collections')
      .select('id, parent_id, user_id')
      .eq('id', parentId)
      .single();
    if (!parent) return false;
    if (parent.user_id !== ownerId) return false;
    return parent.parent_id === null;
  }

  async moveFolderTo(folderId: string, parentId: string | null): Promise<void> {
    if (parentId) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      const canNest = await this.canNestUnder(parentId, user.id);
      if (!canNest) throw new Error('Cannot nest here — maximum folder depth reached');
    }
    const { error } = await supabase
      .from('media_collections')
      .update({ parent_id: parentId })
      .eq('id', folderId);
    if (error) throw error;
  }

  async moveToFolder(mediaIds: string[], collectionId: string | null): Promise<void> {
    for (const id of mediaIds) {
      const { error } = await supabase
        .from('media')
        .update({ collection_id: collectionId })
        .eq('id', id);

      if (error) throw new Error(`Failed to move item ${id}: ${error.message}`);
    }
  }

  convertToMediaFile(record: MediaRecord): MediaFile {
    const bucket = record.storage_bucket || this.BUCKET_NAME;
    const publicUrl = /^https?:\/\//i.test(record.url)
      ? record.url
      : supabase.storage.from(bucket).getPublicUrl(record.url).data.publicUrl;
    const mime = record.type || '';
    const kind: MediaFile['type'] = mime.startsWith('video/')
      ? 'video'
      : mime.startsWith('image/')
        ? 'image'
        : 'document';

    // Resolve the video poster the SAME way as `url` above: cover_image_url is
    // a storage PATH for uploaded/generated clips, not a public URL. Left raw,
    // it never string-matches the fully-resolved public URL that draft posts
    // store in media_urls, so the "In a post" badge silently never renders for
    // video drafts (join is by URL equality in PlanDetailView.usedInPost).
    const resolveThumb = (raw: string) =>
      /^https?:\/\//i.test(raw)
        ? raw
        : supabase.storage.from(bucket).getPublicUrl(raw).data.publicUrl;
    const thumbnail =
      kind === 'image'
        ? publicUrl
        : kind === 'video' && record.cover_image_url
          ? resolveThumb(record.cover_image_url)
          : undefined;

    return {
      id: record.id,
      url: publicUrl,
      type: kind,
      mime,
      name: record.file_name,
      size: record.file_size,
      thumbnail,
      width: record.width,
      height: record.height,
      created_at: record.created_at,
      collection_id: record.collection_id,
    };
  }

  async saveGeneratedImagesForDraft(
    images: Array<{ url: string; metadata?: any } | string>,
  ): Promise<{ data: string[] | null; error: Error | null }> {
    try {
      const savedUrls: string[] = [];
      // D19b: idempotency. Pass 2 wired generate-image.ts to write a canonical
      // `media` row at generation time. This save-for-draft path used to fire
      // a SECOND row write (download → recompress → upload) for every Inspector
      // "Save" / completion event, leaving a ghost `generated_*.webp` row with
      // no prompt/model metadata. Now we check first: if `media.url` or
      // `media.source_ref` already references this URL for the current user,
      // skip the redundant write.
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const ownerId = user?.id ? await resolveOwnerId(user.id) : undefined;

      for (const image of images) {
        const imageUrl = typeof image === 'string' ? image : image.url;

        if (ownerId) {
          const { data: existing } = await supabase
            .from('media')
            .select('id, url')
            .eq('user_id', ownerId)
            .or(`source_ref.eq.image:${imageUrl},url.eq.${imageUrl}`)
            .limit(1)
            .maybeSingle();
          if (existing) {
            savedUrls.push(imageUrl);
            continue;
          }
        }

        const response = await fetch(imageUrl);
        if (!response.ok) {
          logger.error(`Failed to fetch image from ${imageUrl}`);
          continue;
        }

        const blob = await response.blob();
        const fileName = `generated_${Date.now()}_${Math.random().toString(36).substring(7)}.${blob.type.includes('webp') ? 'webp' : 'jpg'}`;
        let file = new File([blob], fileName, { type: blob.type });

        if (
          file.type.startsWith('image/') &&
          file.type !== 'image/svg+xml' &&
          file.type !== 'image/gif'
        ) {
          try {
            if (import.meta.env.DEV)
              logger.debug(
                `Compressing generated image: ${fileName} (${formatFileSize(file.size)})`,
              );
            const { compressImage, getRecommendedQuality } =
              await import('@/services/media/imageCompression');
            const quality = getRecommendedQuality(file.size, false);
            const compressedFile = await compressImage(file, {
              quality,
              format: 'auto',
              useWebWorker: true,
            });
            if (import.meta.env.DEV)
              logger.debug(
                `Compressed: ${formatFileSize(file.size)} → ${formatFileSize(compressedFile.size)}`,
              );
            file = compressedFile;
          } catch (compressionError) {
            logger.warn(`Failed to compress generated image, using original:`, compressionError);
          }
        }

        const uploadResult = await this.uploadFile(file, {
          tags: ['ai-generated'],
        });

        savedUrls.push(uploadResult.publicUrl);
      }

      return { data: savedUrls, error: null };
    } catch (error) {
      logger.error('Failed to save generated images:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Failed to save generated images'),
      };
    }
  }

  async getMediaById(id: string): Promise<MediaRecord | null> {
    const { data, error } = await supabase
      .from('media')
      .select(
        'id, user_id, file_name, url, type, file_size, width, height, alt_text, folder, collection_id, tags, metadata, created_at, storage_bucket, duration_seconds, aspect_ratio, source, source_ref, cover_image_url',
      )
      .eq('id', id)
      .maybeSingle();
    if (error) {
      if (import.meta.env.DEV) logger.warn('[mediaService] getMediaById failed', error);
      return null;
    }
    return (data as MediaRecord | null) ?? null;
  }

  async patchMetadata(mediaId: string, patch: Record<string, unknown>): Promise<void> {
    const { data: existing, error: readErr } = await supabase
      .from('media')
      .select('metadata')
      .eq('id', mediaId)
      .single();
    if (readErr) throw readErr;
    const merged: Record<string, unknown> = {
      ...((existing?.metadata as Record<string, unknown>) ?? {}),
      ...patch,
    };
    if (patch.phash && merged.phash_status === 'pending_client_rehash') {
      delete merged.phash_status;
    }
    const { error: updErr } = await supabase
      .from('media')
      .update({ metadata: merged })
      .eq('id', mediaId);
    if (updErr) throw updErr;
  }

  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ width: 0, height: 0 });
      };

      img.src = url;
    });
  }
}

export const mediaService = new MediaService();
export type { MediaUploadResult, MediaRecord };
